// API client for /api/questionnaires (coach library: questionnaires + tracked
// variables). Same Bearer-from-localStorage pattern as TemplatesPage.
import { getApiBaseUrlWithApi } from '../config/api';

export class QuestionnairesApiError extends Error {
  constructor(message, { status, code, errors } = {}) {
    super(message);
    this.name = 'QuestionnairesApiError';
    this.status = status;
    this.code = code;
    // Per-question error codes of a rejected submission ({ [questionId]: code }).
    this.errors = errors;
  }
}

const request = async (path, options = {}) => {
  const token = localStorage.getItem('authToken');
  const res = await fetch(`${getApiBaseUrlWithApi()}/questionnaires${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new QuestionnairesApiError(body?.message || body?.error || `Request failed (${res.status})`, {
      status: res.status,
      code: body?.code,
      errors: body?.errors,
    });
  }
  return body?.data;
};

const json = (method, payload) => ({ method, body: JSON.stringify(payload ?? {}) });

export const isPlanRestricted = (err) => err?.code === 'PLAN_RESTRICTED';

export const questionnairesApi = {
  listVariables: ({ includeArchived = false } = {}) =>
    request(`/variables${includeArchived ? '?include_archived=true' : ''}`),
  createVariable: (payload) => request('/variables', json('POST', payload)),
  updateVariable: (id, payload) => request(`/variables/${id}`, json('PATCH', payload)),
  // The coach's order of their variables (most followed first).
  reorderVariables: (ids) => request('/variables/order', json('PUT', { ids })),
  archiveVariable: (id) => request(`/variables/${id}`, { method: 'DELETE' }),

  listQuestionnaires: ({ includeArchived = false } = {}) =>
    request(includeArchived ? '?include_archived=true' : ''),
  createQuestionnaire: (payload) => request('', json('POST', payload)),
  updateQuestionnaire: (id, payload) => request(`/${id}`, json('PATCH', payload)),
  archiveQuestionnaire: (id) => request(`/${id}`, { method: 'DELETE' }),
  duplicateQuestionnaire: (id, payload) => request(`/${id}/duplicate`, json('POST', payload)),
  // Answered questionnaires on the coach's demo student (showcase). Without
  // samples, the API generates answers for what the demo student receives.
  seedDemo: (payload) => request('/demo', json('POST', payload)),

  // Students' answers not opened yet (onboarding, weekly check-ins)
  // -> { unread, byStudent }; « read » once the coach opened one.
  responsesSummary: () => request('/responses/summary'),
  markResponseSeen: (id) => request(`/responses/${id}/seen`, json('POST')),
};

export const studentQuestionnairesApi = {
  get: (studentId) => request(`/students/${studentId}`),
  updateSettings: (studentId, kind, payload) =>
    request(`/students/${studentId}/settings/${kind}`, json('PUT', payload)),
  sendOnboarding: (studentId) => request(`/students/${studentId}/onboarding`, { method: 'POST' }),
  variableValues: (studentId) => request(`/students/${studentId}/variable-values`),
  // End-of-session answers of one session (null when none).
  sessionResponse: (assignmentId) => request(`/assignments/${assignmentId}/session-response`),
  // Every end-of-session answer of one student (latest first).
  studentSessionResponses: (studentId) => request(`/students/${studentId}/session-responses`),
};

// Student side: what to answer, and answering.
export const myQuestionnairesApi = {
  pending: () => request('/me/pending'),
  // « Mes bilans »: past check-ins (answered or missed) and own curves.
  history: () => request('/me/history'),
  variableValues: () => request('/me/variable-values'),
  get: (responseId) => request(`/me/responses/${responseId}`),
  submit: (responseId, answers) => request(`/me/responses/${responseId}/submit`, json('POST', { answers })),
  // End of session: the questions that apply ({ id, title, questions } or null), answering them.
  sessionQuestionnaire: () => request('/me/session-questionnaire'),
  submitSession: (assignmentId, questionnaireId, answers) => request('/me/session-answers', json('POST', { assignmentId, questionnaireId, answers })),
};
